import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MathfieldElement } from "mathlive";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const QUESTIONS = [
  { id: "q1", instruction: "Write as a single power of 2.", latex: "2^3 \\times 2^4", answers: ["2^7"], displayAnswer: "2^7" },
  { id: "q2", instruction: "Write as a single power of 3.", latex: "3^8 \\div 3^5", answers: ["3^3"], displayAnswer: "3^3" },
  { id: "q3", instruction: "Simplify.", latex: "x^4 \\times x^3", answers: ["x^7"], displayAnswer: "x^7" },
  { id: "q4", instruction: "Simplify.", latex: "a^9 \\div a^4", answers: ["a^5"], displayAnswer: "a^5" },
  { id: "q5", instruction: "Simplify.", latex: "\\left(y^2\\right)^5", answers: ["y^10"], displayAnswer: "y^{10}" },
  { id: "q6", instruction: "Simplify.", latex: "m^6 \\div m^6", answers: ["1"], displayAnswer: "1" },
  { id: "q7", instruction: "Simplify.", latex: "\\left(2x^2\\right)^3", answers: ["8x^6"], displayAnswer: "8x^6" },
  { id: "q8", instruction: "Write as a single power of 2.", latex: "2^3 \\times 4^2", answers: ["2^7"], displayAnswer: "2^7" },
  { id: "q9", instruction: "Write as a single power of 3.", latex: "27^2 \\div 3^3", answers: ["3^3"], displayAnswer: "3^3" },
  { id: "q10", instruction: "Simplify fully.", latex: "a^3b^2 \\times a^4b^5", answers: ["a^7b^7", "b^7a^7"], displayAnswer: "a^7b^7" },
  { id: "q11", instruction: "Simplify fully.", latex: "\\left(x^3y^2\\right)^2", answers: ["x^6y^4", "y^4x^6"], displayAnswer: "x^6y^4" },
  { id: "q12", instruction: "Write as a single power of 2.", latex: "\\frac{4^3 \\times 8^2}{2^5}", answers: ["2^7"], displayAnswer: "2^7" },
  { id: "q13", instruction: "Simplify fully, leaving positive indices.", latex: "\\frac{p^3q^7}{p^5q^2}", answers: ["q^5/p^2", "1/p^2*q^5", "q^5p^-2"], displayAnswer: "\\frac{q^5}{p^2}" },
  { id: "q14", instruction: "Simplify fully.", latex: "\\frac{\\left(r^3\\right)^4 \\times r^2}{r^5}", answers: ["r^9"], displayAnswer: "r^9" },
];

const STORAGE_KEY = "laws_of_indices_quiz_v2";
const SUBMIT_URL =
  `${process.env.REACT_APP_HOMEWORK_API_URL || (process.env.NODE_ENV === "development" ? "http://localhost:5001" : "")}/api/homework/laws-of-indices`;

function normalize(raw) {
  let value = String(raw ?? "")
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "$1/$2")
    .replace(/\\cdot|\\times/g, "*")
    .replace(/\\div/g, "/")
    .replace(/\\,/g, "")
    .replace(/\s+/g, "")
    .replace(/[{}()]/g, "");

  return value.replace(/\*/g, "");
}

function makeSubmissionId() {
  if (
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function ResultIcon({ type, size = 28 }) {
  const isCorrect = type === "correct";
  const colour = isCorrect ? "#138a5b" : "#d64545";
  const paleColour = isCorrect ? "#e7f7ef" : "#fdecec";

  return (
    <svg
      aria-label={isCorrect ? "Correct" : "Incorrect"}
      role="img"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <circle cx="16" cy="16" r="14" fill={paleColour} stroke={colour} strokeWidth="2" />
      {isCorrect ? (
        <path
          d="M9.2 16.2l4.3 4.4 9.5-10"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M11 11l10 10M21 11L11 21"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export default function LawsOfIndicesQuiz() {
  const navigate = useNavigate();
  const questions = useMemo(() => QUESTIONS, []);

  const [studentName, setStudentName] = useState("");
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [current, setCurrent] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState("");
  const [working, setWorking] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [questionResults, setQuestionResults] = useState(Array(questions.length).fill(null));
  const [studentAnswers, setStudentAnswers] = useState(Array(questions.length).fill(""));
  const [workingAnswers, setWorkingAnswers] = useState(Array(questions.length).fill(""));
  const [attemptHistory, setAttemptHistory] = useState(Array.from({ length: questions.length }, () => []));
  const [reviewMode, setReviewMode] = useState(false);
  const [done, setDone] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState("idle");
  const [submissionId, setSubmissionId] = useState(makeSubmissionId);

  useEffect(() => {
    if (!customElements.get("math-field")) customElements.define("math-field", MathfieldElement);
  }, []);

  useEffect(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!data) return;
      setStudentName(data.studentName || "");
      setNameConfirmed(Boolean(data.nameConfirmed));
      setCurrent(Number.isInteger(data.current) ? data.current : 0);
      if (Array.isArray(data.questionResults) && data.questionResults.length === questions.length) setQuestionResults(data.questionResults);
      if (Array.isArray(data.studentAnswers) && data.studentAnswers.length === questions.length) setStudentAnswers(data.studentAnswers);
      if (Array.isArray(data.workingAnswers) && data.workingAnswers.length === questions.length) setWorkingAnswers(data.workingAnswers);
      if (Array.isArray(data.attemptHistory) && data.attemptHistory.length === questions.length) setAttemptHistory(data.attemptHistory);
      setDone(Boolean(data.done));
      setSubmissionStatus(data.submissionStatus || "idle");
      setSubmissionId(data.submissionId || makeSubmissionId());
      const index = Number.isInteger(data.current) ? data.current : 0;
      setStudentAnswer(data.studentAnswers?.[index] || "");
      setWorking(data.workingAnswers?.[index] || "");
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [questions.length]);

  const saveProgress = (override = {}) => {
    const payload = {
      studentName, nameConfirmed, current, questionResults, studentAnswers,
      workingAnswers, attemptHistory, done, submissionStatus, submissionId, ...override,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const confirmName = (event) => {
    event.preventDefault();
    const cleanName = studentName.trim();
    if (!cleanName) return;
    setStudentName(cleanName);
    setNameConfirmed(true);
    saveProgress({ studentName: cleanName, nameConfirmed: true });
  };

  const checkAnswer = () => {
    if (!studentAnswer.trim()) {
      setFeedback("Please enter an answer first.");
      return;
    }

    const q = questions[current];
    const isCorrect = q.answers.some((answer) => normalize(answer) === normalize(studentAnswer));
    void playQuizFeedback(isCorrect);
    const updatedStudentAnswers = [...studentAnswers];
    const updatedWorkingAnswers = [...workingAnswers];
    const updatedHistory = attemptHistory.map((history) => [...history]);
    updatedStudentAnswers[current] = studentAnswer;
    updatedWorkingAnswers[current] = working;
    updatedHistory[current].push(studentAnswer);

    const updatedResults = [...questionResults];
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    setStudentAnswers(updatedStudentAnswers);
    setWorkingAnswers(updatedWorkingAnswers);
    setAttemptHistory(updatedHistory);

    if (isCorrect) {
      updatedResults[current] = "correct";
      setQuestionResults(updatedResults);
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="correct" />
          <strong style={{ color: "#087f5b" }}>Correct!</strong>
        </div>
      );
    } else if (newAttempts >= 3) {
      updatedResults[current] = "incorrect";
      setQuestionResults(updatedResults);
      setFeedback(
        <div>
          <div style={styles.statusLine}>
            <ResultIcon type="incorrect" />
            <strong style={{ color: "#c0392b" }}>Not quite. The correct answer is:</strong>
          </div>
          <BlockMath math={q.displayAnswer} />
          <div style={{ marginTop: 6, opacity: 0.8, fontSize: 14 }}>You can move on now.</div>
        </div>
      );
    } else {
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="incorrect" />
          <strong style={{ color: "#c0392b" }}>
            Try again. Attempts left: {3 - newAttempts}
          </strong>
        </div>
      );
    }

    saveProgress({
      studentAnswers: updatedStudentAnswers,
      workingAnswers: updatedWorkingAnswers,
      attemptHistory: updatedHistory,
      questionResults: updatedResults,
    });
  };

  const canAdvance = questionResults[current] === "correct" || attempts >= 3;

  const submitResults = async (results, answers, histories, workings) => {
    setSubmissionStatus("sending");
    saveProgress({ done: true, questionResults: results, studentAnswers: answers, attemptHistory: histories, workingAnswers: workings, submissionStatus: "sending" });

    const payload = {
      submissionId,
      studentName: studentName.trim(),
      quizTitle: "Laws of Indices",
      completedAt: new Date().toISOString(),
      score: results.filter((result) => result === "correct").length,
      total: questions.length,
      questions: questions.map((question, index) => ({
        number: index + 1,
        question: `${question.instruction} ${question.latex}`,
        correctAnswer: question.displayAnswer,
        studentAnswer: answers[index] || "No answer",
        attempts: histories[index] || [],
        working: workings[index] || "",
        result: results[index] || "incorrect",
      })),
    };

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Submission failed");
      setSubmissionStatus("sent");
      saveProgress({ done: true, submissionStatus: "sent" });
    } catch {
      setSubmissionStatus("failed");
      saveProgress({ done: true, submissionStatus: "failed" });
    }
  };

  const nextQuestion = () => {
    setFeedback(null);
    setAttempts(0);
    const next = current + 1;

    if (next < questions.length) {
      setCurrent(next);
      setStudentAnswer(studentAnswers[next] || "");
      setWorking(workingAnswers[next] || "");
      saveProgress({ current: next });
      return;
    }

    if (reviewMode) {
      navigate("/student-dashboard");
      return;
    }

    setDone(true);
    saveProgress({ done: true });
    if (submissionStatus !== "sent") {
      submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
    }
  };

  const previousQuestion = () => {
    if (current === 0) return;
    const previous = current - 1;
    setCurrent(previous);
    setFeedback(null);
    setAttempts(attemptHistory[previous]?.length || 0);
    setStudentAnswer(studentAnswers[previous] || "");
    setWorking(workingAnswers[previous] || "");
    saveProgress({ current: previous });
  };

  const startReview = () => {
    setReviewMode(true);
    setCurrent(0);
    setFeedback(null);
    setStudentAnswer(studentAnswers[0] || "");
    setWorking(workingAnswers[0] || "");
  };

  const retrySubmission = () => submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
  const score = questionResults.filter((result) => result === "correct").length;

  const feedbackMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return "🌟 Perfect—you got everything right!";
    if (percentage >= 80) return "🎉 Great job—you did really well!";
    if (percentage >= 50) return "👍 Good effort. Review any red questions.";
    return "💪 Keep practising. Reviewing your answers will help.";
  };

  const renderProgress = () => (
    <div style={styles.progressWrapper}>
      {questionResults.map((result, index) => (
        <div key={questions[index].id} style={{
          ...styles.progressDot,
          backgroundColor: result === "correct" ? "#00b894" : result === "incorrect" ? "#d63031" : "#636e72",
          border: index === current && !done ? "3px solid #f9b710" : "2px solid transparent",
        }}>
          {index + 1}
        </div>
      ))}
    </div>
  );

  if (!nameConfirmed) {
    return (
  
        <div style={styles.container}>
          <div style={styles.nameCard}>
            <h1 style={styles.heading}>Laws of Indices Quiz</h1>
            <p>Please enter your name before you begin. Your results will be sent to Ryan when you finish.</p>
            <form onSubmit={confirmName}>
              <label htmlFor="student-name" style={styles.label}>Your name</label>
              <input id="student-name" autoFocus autoComplete="name" maxLength={80} value={studentName}
                onChange={(event) => setStudentName(event.target.value)} style={styles.nameInput} />
              <button type="submit" disabled={!studentName.trim()} style={styles.startBtn}>Start Quiz ➡️</button>
            </form>
          </div>
        </div>
   
    );
  }

  const q = questions[current];
  const currentResult = questionResults[current];
  const completedFieldStyle =
    currentResult === "correct"
      ? styles.correctField
      : currentResult === "incorrect"
        ? styles.incorrectField
        : styles.unansweredField;

  return (
       <div style={styles.container}>
        <h1 style={styles.heading}>Laws of Indices</h1>
        <p style={styles.studentLabel}>Student: <strong>{studentName}</strong></p>
        {renderProgress()}

        {done && !reviewMode ? (
          <div style={styles.resultsBox}>
            <h2 style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#17324d", fontWeight: 700 }}>Score: {score} / {questions.length}</h2>
            <p style={styles.feedbackText}>{feedbackMessage()}</p>
            {submissionStatus === "sending" && <p>Sending your results to Ryan…</p>}
            {submissionStatus === "sent" && (
              <div style={{ ...styles.statusLine, ...styles.sentMessage }}>
                <ResultIcon type="correct" size={25} />
                <span>Your results have been sent to Ryan.</span>
              </div>
            )}
            {submissionStatus === "failed" && (
              <div>
                <p style={styles.errorMessage}>Your quiz is saved, but the results could not be sent.</p>
                <button onClick={retrySubmission} style={styles.submitBtn}>Try Sending Again</button>
              </div>
            )}
            <button onClick={startReview} style={styles.reviewBtn}>🔍 Review Questions</button>
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.questionHeading}>Question {current + 1} of {questions.length}</h3>
            <div style={styles.calculatorIndicator}>
              <CalculatorIcon allowed={q.calculator === "yes"} />
            </div>
            <p style={styles.instruction}>{q.instruction}</p>
            <div className="wrap-math"><BlockMath math={q.latex} /></div>

            <label style={styles.label}>Show your working:</label>
            <textarea rows={4} readOnly={reviewMode || canAdvance}
              value={reviewMode ? workingAnswers[current] : working}
              onChange={(event) => setWorking(event.target.value)}
              placeholder="Show any working here" style={{ ...styles.workingInput, ...completedFieldStyle }} />

            <label style={styles.label}>Answer:</label>
            <math-field readOnly={reviewMode || canAdvance} style={{ ...styles.mathInput, ...completedFieldStyle }}
              value={reviewMode || canAdvance ? studentAnswers[current] : studentAnswer}
              onInput={(event) => !reviewMode && setStudentAnswer(event.target.value)} />

            {reviewMode && (
              <div style={styles.reviewAnswer}>
                <div style={styles.statusLine}>
                  <ResultIcon type={questionResults[current] === "correct" ? "correct" : "incorrect"} />
                  <strong style={{ color: questionResults[current] === "correct" ? "#087f5b" : "#c0392b" }}>
                    {questionResults[current] === "correct" ? "Correct:" : "Correct answer:"}
                  </strong>
                </div>
                <BlockMath math={q.displayAnswer} />
              </div>
            )}
            {!reviewMode && feedback && <div style={styles.feedbackBox}>{feedback}</div>}
            {!reviewMode && !canAdvance && <button onClick={checkAnswer} style={styles.submitBtn}>Check Answer</button>}

            {(canAdvance || reviewMode) && (
              <div>
                {current > 0 && <button onClick={previousQuestion} style={styles.secondaryBtn}>← Back</button>}
                <button onClick={nextQuestion} style={styles.nextBtn}>
                  {current + 1 < questions.length ? "Next ➡️" : reviewMode ? "Finish Review" : "Finish & Send 🎉"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    
  );
}

const styles = {
  container: { padding: "2rem", fontFamily: "'Fredoka', cursive", backgroundColor: "#eaf4fc", minHeight: "100vh" },
  heading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.035em", color: "#17324d", textAlign: "center", maxWidth: 760, margin: "0 auto 1.5rem", paddingBottom: "1rem", borderBottom: "3px solid #b8d4e8" },
  questionHeading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "1rem", fontWeight: 600, letterSpacing: "0.025em", color: "#475569", margin: "0 0 1.25rem" },
  studentLabel: { textAlign: "center", marginBottom: "1rem" },
  card: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 700, margin: "1rem auto 0" },
  nameCard: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 520, margin: "4rem auto" },
  nameInput: { display: "block", width: "100%", boxSizing: "border-box", padding: "0.75rem", margin: "0.5rem 0 1rem", border: "1px solid #aaa", borderRadius: 8, fontSize: "1.1rem" },
  startBtn: { padding: "0.7rem 1.3rem", backgroundColor: "#6c5ce7", color: "white", fontWeight: "bold", border: 0, borderRadius: 7, cursor: "pointer" },
  calculatorIndicator: { marginBottom: 12, display: "flex", justifyContent: "flex-end" },
  instruction: { fontWeight: 700, fontSize: "1.05rem" },
  label: { fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#334155", fontWeight: 600, marginTop: 10, display: "block" },
  mathInput: { display: "block", width: "100%", boxSizing: "border-box", fontSize: "1.25rem", padding: "0.65rem", borderRadius: 8, border: "1px solid #ccc", marginTop: 8 },
  workingInput: { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #ccc", padding: "0.75rem", fontSize: "1rem", fontFamily: "'Fredoka', cursive", marginTop: 8, marginBottom: 20 },
  unansweredField: { backgroundColor: "#fff", border: "1px solid #ccc" },
  correctField: { backgroundColor: "#e8f8ee", border: "2px solid #42a96b" },
  incorrectField: { backgroundColor: "#fdeaea", border: "2px solid #d95858" },
  feedbackBox: { marginTop: "1rem" },
  statusLine: { display: "flex", alignItems: "center", gap: 10 },
  reviewAnswer: { marginTop: "1rem", padding: "1rem", backgroundColor: "#f5f6fa", borderRadius: 8 },
  submitBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#0984e3", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  nextBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#00b894", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
  secondaryBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  progressWrapper: { display: "flex", justifyContent: "center", gap: 10, marginBottom: "1.5rem", flexWrap: "wrap" },
  progressDot: { width: 36, height: 36, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: "bold", color: "white" },
  resultsBox: { textAlign: "center", backgroundColor: "white", maxWidth: 700, margin: "2rem auto", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
  feedbackText: { fontSize: "1.25rem", margin: "1rem 0" },
  sentMessage: { color: "#087f5b", fontWeight: 700, justifyContent: "center", margin: "1rem 0" },
  errorMessage: { color: "#c0392b", fontWeight: 700 },
  reviewBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
};


function CalculatorIcon({ allowed }) {
  const label = allowed ? "Calculator allowed" : "No calculator";

  return (
    <svg role="img" aria-label={label} width={56} height={40} viewBox="0 0 56 40"
      style={{ display: "block" }}>
      <title>{label}</title>
      <rect x="5" y="3" width="27" height="34" rx="4" fill="#f1f5f9" stroke="#334155" strokeWidth="2" />
      <rect x="10" y="8" width="17" height="7" rx="1" fill="#cbd5e1" stroke="#334155" />
      <path d="M11 21h2m5 0h2m5 0h2M11 27h2m5 0h2m5 0h2M11 32h2m5 0h2m5 0h2"
        stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      {allowed ? (
        <path d="M37 22l5 5 9-12" fill="none" stroke="#138a5b" strokeWidth="4"
          strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M5 5l27 30M32 5L5 35" fill="none" stroke="#d64545" strokeWidth="4"
          strokeLinecap="round" />
      )}
    </svg>
  );
}


// Generate short feedback tones locally, without loading audio files.
let quizAudioContext;
async function playQuizFeedback(correct) {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!quizAudioContext || quizAudioContext.state === "closed") quizAudioContext = new AudioContextClass();
    if (quizAudioContext.state === "suspended") await quizAudioContext.resume();
    const context = quizAudioContext;
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const volume = context.createGain();
    oscillator.type = correct ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(correct ? 880 : 240, start);
    if (correct) oscillator.frequency.setValueAtTime(1174.66, start + 0.07);
    else {
      oscillator.frequency.exponentialRampToValueAtTime(170, start + 0.1);
      oscillator.frequency.setValueAtTime(210, start + 0.14);
      oscillator.frequency.exponentialRampToValueAtTime(130, start + 0.28);
    }
    const duration = correct ? 0.2 : 0.3;
    volume.gain.setValueAtTime(0.0001, start);
    volume.gain.exponentialRampToValueAtTime(correct ? 0.15 : 0.14, start + 0.008);
    if (!correct) {
      volume.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
      volume.gain.setValueAtTime(0.0001, start + 0.14);
      volume.gain.exponentialRampToValueAtTime(0.14, start + 0.15);
    }
    volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(volume);
    volume.connect(context.destination);
    oscillator.onended = () => { oscillator.disconnect(); volume.disconnect(); };
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  } catch {
    // Audio availability should never interrupt answer checking.
  }
}
